import type { ProductLifecycleState } from "@/types/product";

export enum ProductTab {
  SeoInsights = "seo-insights",
  KeywordBank = "keyword-bank",
  KeywordLandscapes = "keyword-landscapes",
  ABTests = "ab-tests",
  SearchTerms = "search-terms",
  SpiScorecard = "spi-scorecard",
}

/**
 * The three storable lifecycle states, in the order every surface lists them:
 * the modal's radio options and the filter group's options both read this tuple.
 * `data-model.md § ProductLifecycleState`; order from
 * `design-input/product-lifecycle-states/prototype-extracted.md § The modal`.
 */
export const LIFECYCLE_STATES = ["priority", "core", "unassigned"] as const;

/**
 * Compile-time guard: every member of `ProductLifecycleState` must appear in `LIFECYCLE_STATES`.
 *
 * The union and this tuple are declared independently — the union is the API contract mirror and
 * has to stay in `src/types/` (`conventions/08-api-contracts.md`), while the tuple drives the
 * modal's radio order and the filter's options. Without this assertion, adding a fourth state to
 * the union and forgetting the tuple is silent: the three `Record<ProductLifecycleState, …>` maps
 * below are exhaustiveness-checked and would demand their entries, but the new state would simply
 * never render in the dialog or the filter, with every gate green. This turns that into a type
 * error at the one line that would otherwise say nothing.
 */
type EveryStateIsListed =
  Exclude<ProductLifecycleState, (typeof LIFECYCLE_STATES)[number]> extends never ? true : never;
const _everyStateIsListed: EveryStateIsListed = true;
void _everyStateIsListed;

/** Verbatim from `signal-v3/src/lib/lifecycleStateStore.ts:27-31`. */
export const LIFECYCLE_LABELS: Record<ProductLifecycleState, string> = {
  priority: "Priority",
  core: "Core",
  unassigned: "Unassigned",
};

/**
 * Verbatim from `signal-v3/src/lib/lifecycleStateStore.ts` — em dash included.
 *
 * `unassigned` was re-worded by AUT-435's 2026-10-07 revision. The old copy ("Shows no badge
 * anywhere in the app") described the behaviour that revision removed: the state now carries a
 * dashed tag wherever it can be changed, so the sentence would have been a lie in the very key
 * that renders beside it.
 */
export const LIFECYCLE_DESCRIPTIONS: Record<ProductLifecycleState, string> = {
  priority: "Actively focused — currently being worked on.",
  core: "Important to the portfolio, but not the current focus.",
  unassigned: "No designation yet. Not marked as Priority or Core.",
};

/**
 * Compared numerically by the Lifecycle column, so `desc` puts Priority first
 * (`signal-v3/src/pages/Products.tsx:381`). Index it only via `lifecycleStateOf`:
 * `strictNullChecks` is off, so an undefined key yields `NaN`, and a comparator
 * returning `NaN` scrambles the order silently instead of throwing.
 */
export const LIFECYCLE_SORT_WEIGHT: Record<ProductLifecycleState, number> = {
  priority: 3,
  core: 2,
  unassigned: 1,
};

/**
 * The single declaration of the "absent means unassigned" fallback in the SPA.
 * `ProductListItem.lifecycle_state` is optional, so every non-rendering reader
 * normalises through this; rendering readers get it from `LifecycleStateBadge`.
 */
export function lifecycleStateOf(
  state: ProductLifecycleState | undefined,
): ProductLifecycleState {
  return state ?? "unassigned";
}
